/**
 * Payout settlement queue (ops).
 *
 * Every client redemption leg funds confirms lands here as a payout
 * instruction. The queue walks each one through the settlement state machine:
 *
 *   pending_execution ─(client chooses destination)─▶ ─submit (rail trace)─▶ submitted
 *   submitted ─execute (CEP / comprobante, a DIFFERENT manager)─▶ executed
 *   pending_execution | submitted ─fail─▶ failed ─reissue─▶ new instruction
 *   submitted ─return─▶ returned ─reissue─▶ new instruction
 *   executed ─came back after settlement (one manager names the credit, ANOTHER confirms)─▶ returned
 *   executed with no bank debit ─tie bank debit (one manager: evidence, no money moves)─▶ tied
 *
 * Every action is a money-movement step and asks for a fresh authenticator
 * code. Agents submit (the destination is the client's own choice); confirming settlement,
 * failing, returning and reissuing are manager actions (the backend enforces
 * both the role and submitter ≠ executor).
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import DebitPicker from '../../components/staff/DebitPicker';
import payoutsService, {
  type BankDebitCandidate,
  type Payout,
  type PayoutStatus,
  type SettledReturn,
} from '../../services/payouts.service';
import authService from '../../services/auth.service';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';

const STATUS_TONE: Record<PayoutStatus, BadgeTone> = {
  pending_execution: 'warning',
  submitted: 'info',
  executed: 'success',
  failed: 'danger',
  returned: 'danger',
};

/** The status word a person reads on the badge (never the stored value, which is not wording). */
const STATUS_LABELS: Record<PayoutStatus, string> = {
  pending_execution: 'pending execution',
  submitted: 'submitted',
  executed: 'executed',
  failed: 'failed',
  returned: 'returned',
};

type Filter = 'open' | 'returns' | 'nodebit' | 'unresolved' | 'all';
type Action =
  | 'submit' | 'execute' | 'fail' | 'return' | 'reissue'
  | 'settled_return' | 'confirm_return' | 'decline_return'
  | 'tie_debit';

/** What the API accepts as the action's value: a reason of at least 5 characters (fail,
 *  return, a return after settlement, declining one), a rail trace of at least 1 (submit); a
 *  settlement proof of at least 4 otherwise. */
const MIN_VALUE_LENGTH: Partial<Record<Action, number>> = {
  fail: 5, return: 5, submit: 1, settled_return: 5, decline_return: 5,
};

/** The actions whose only input is the authentication code. */
const CODE_ONLY: ReadonlySet<Action> = new Set<Action>(['reissue', 'confirm_return', 'tie_debit']);

const FILTER_LABELS: Record<Filter, string> = {
  open: 'Open',
  returns: 'Returns to confirm',
  nodebit: 'No bank debit',
  unresolved: 'Failed / returned',
  all: 'All',
};

const FILTER_STATUSES: Record<Filter, PayoutStatus[]> = {
  open: ['pending_execution', 'submitted'],
  returns: [],
  nodebit: [],
  unresolved: ['failed', 'returned'],
  all: [],
};

export default function Payouts(): JSX.Element {
  const [filter, setFilter] = useState<Filter>('open');
  const [rows, setRows] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [active, setActive] = useState<{ id: string; action: Action } | null>(null);
  const isManager = authService.getRole() !== 'agent';

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await payoutsService.list(
        FILTER_STATUSES[filter], 100, filter === 'returns', filter === 'nodebit',
      );
      setRows(res.items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the payout queue.'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const done = (msg: string): void => {
    setActive(null);
    setFlash(msg);
    globalThis.setTimeout(() => setFlash(null), 6000);
    reload();
  };

  return (
    <>
      <PageHeader
        title="Payouts"
        subtitle="Client redemptions awaiting bank settlement. Sending records the rail trace; a different manager confirms against the CEP."
        actions={
          <div className="inline-flex rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
            {(['open', 'returns', 'nodebit', 'unresolved', 'all'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className="px-3 py-1.5 text-xs font-semibold"
                style={{
                  background: filter === f ? colors.navy : colors.surface,
                  color: filter === f ? '#FFFFFF' : colors.inkSoft,
                }}
              >
                {FILTER_LABELS[f]}
              </button>
            ))}
          </div>
        }
      />

      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          Nothing in this view.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((p) => (
            <li key={p.id} className="rounded-2xl p-4"
                style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
              <PayoutCard
                payout={p}
                isManager={isManager}
                activeAction={active?.id === p.id ? active.action : null}
                onPick={(action) => setActive(action ? { id: p.id, action } : null)}
                onDone={done}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

interface CardProps {
  payout: Payout;
  isManager: boolean;
  activeAction: Action | null;
  onPick: (action: Action | null) => void;
  onDone: (msg: string) => void;
}

function PayoutCard({ payout: p, isManager, activeAction, onPick, onDone }: Readonly<CardProps>): JSX.Element {
  const stale = p.status === 'pending_execution' || p.status === 'submitted' ? p.age_days >= 3 : false;
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold" style={{ color: colors.ink }}>
            {formatMoney(p.net_amount, p.currency_code)} · {p.product_name}
          </div>
          {p.management_fee_amount && (
            <div className="text-xs" style={{ color: colors.inkSoft }}>
              Management fee deducted: {formatMoney(p.management_fee_amount, p.currency_code)}
            </div>
          )}
          {p.origin === 'departed_share' && (
            <div className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
              Result of a completed term, holder who left
            </div>
          )}
          {p.origin === 'nav_error_compensation' && (
            <div className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
              Owed to the client by a NAV error case
            </div>
          )}
          <div className="text-xs" style={{ color: colors.muted }}>
            {p.client_email} · ref <span className="font-mono">{p.funds_reference_number}</span>
          </div>
          <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
            Destination: {p.destination_label ?? <em>waiting for the client to choose</em>}
            {p.rail_trace_number && <> · trace <span className="font-mono">{p.rail_trace_number}</span></>}
            {p.settlement_proof_ref && <> · proof <span className="font-mono">{p.settlement_proof_ref}</span></>}
          </div>
          {p.resolution_reason && (
            <div className="text-xs mt-1" style={{ color: colors.danger }}>{p.resolution_reason}</div>
          )}
          {p.held_for_estate && (
            <div className="text-xs mt-1 font-semibold" style={{ color: colors.warning }}>
              Held for the estate of a deceased holder: nothing is sent until the estate can be settled.
            </div>
          )}
          {p.settled_return && <SettledReturnNote settled={p.settled_return} />}
          {p.debit_tied_at && (
            <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
              Bank debit tied after settlement on {formatDateTime(p.debit_tied_at)}.
            </div>
          )}
          {settledWithoutDebit(p) && (
            <div className="text-xs mt-1 font-semibold" style={{ color: colors.warning }}>
              No bank debit is tied to this settled payout yet.
            </div>
          )}
          {!p.settlement_notified && (
            <div className="text-xs mt-1 font-semibold" style={{ color: colors.danger }}>
              {p.settlement_delivery_abandoned
                ? 'Accounting was never told this settled — delivery gave up. Ops must re-drive the event.'
                : 'Accounting has not confirmed this settlement yet.'}
            </div>
          )}
        </div>
        <div className="text-right">
          <StatusBadge tone={STATUS_TONE[p.status]}>{STATUS_LABELS[p.status]}</StatusBadge>
          <div className="text-xs mt-1" style={{ color: stale ? colors.warning : colors.mutedSoft }}>
            {formatDateTime(p.created_at)} · {p.age_days}d{stale ? ' · stale' : ''}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {p.status === 'pending_execution' && !p.held_for_estate && (
          <ActionButton label="Mark sent" primary onClick={() => onPick('submit')} />
        )}
        {p.status === 'submitted' && isManager && (
          <>
            <ActionButton label="Confirm settlement" primary onClick={() => onPick('execute')} />
            <ActionButton label="Returned by bank" danger onClick={() => onPick('return')} />
          </>
        )}
        {(p.status === 'pending_execution' || p.status === 'submitted') && isManager && (
          <ActionButton label="Mark failed" danger onClick={() => onPick('fail')} />
        )}
        {settledWithoutDebit(p) && isManager && (
          <ActionButton label="Tie bank debit" onClick={() => onPick('tie_debit')} />
        )}
        {p.status === 'executed' && isManager && !p.settled_return && p.origin === 'redemption' && (
          <ActionButton label="Came back after settlement" danger onClick={() => onPick('settled_return')} />
        )}
        {p.settled_return?.status === 'proposed' && isManager && (
          <>
            <ActionButton label="Confirm return" primary onClick={() => onPick('confirm_return')} />
            <ActionButton label="Decline return" onClick={() => onPick('decline_return')} />
          </>
        )}
        {(p.status === 'failed' || p.status === 'returned') && isManager && (
          <ActionButton label="Reissue" primary onClick={() => onPick('reissue')} />
        )}
      </div>

      {activeAction && (
        <ActionPanel payout={p} action={activeAction} onCancel={() => onPick(null)} onDone={onDone} />
      )}
    </div>
  );
}

/** The money left (settled, or returned after settlement) and no bank debit is tied to it yet. */
function settledWithoutDebit(p: Payout): boolean {
  return (p.status === 'executed' || p.status === 'returned')
    && p.executed_at !== null && p.settled_bank_transaction_id === null;
}

/** A return after settlement on the card: what came back and where it stands. */
function SettledReturnNote({ settled }: Readonly<{ settled: SettledReturn }>): JSX.Element {
  return (
    <div className="text-xs mt-1" style={{ color: colors.danger }}>
      <div className="font-semibold">
        {settled.status === 'proposed'
          ? 'Came back after settlement: waiting for a second manager to confirm.'
          : 'Came back after settlement: confirmed. The client is still owed.'}
      </div>
      <div>
        Credit of {formatDateTime(settled.credit_posted_at)} · bank reference{' '}
        <span className="font-mono" translate="no">{settled.bank_return_reference}</span>
      </div>
      <div>{settled.reason}</div>
      {!settled.books_notified && (
        <div className="font-semibold">
          {settled.books_delivery_abandoned
            ? 'Accounting was never told this payout came back — delivery gave up. Ops must re-drive the event.'
            : 'Accounting has not confirmed this return yet.'}
        </div>
      )}
    </div>
  );
}

function ActionButton({ label, onClick, primary, danger }: Readonly<{
  label: string; onClick: () => void; primary?: boolean; danger?: boolean;
}>): JSX.Element {
  let style: React.CSSProperties = { background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` };
  if (primary) style = { background: gradients.gold, color: colors.navy };
  if (danger) style = { background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` };
  return (
    <button type="button" onClick={onClick} className="px-3 py-1.5 rounded-md text-xs font-semibold" style={style}>
      {label}
    </button>
  );
}

const PANEL_COPY: Record<Action, { title: string; hint: string }> = {
  submit: { title: 'Mark as sent', hint: 'Copy the rail\'s own identifier from the bank\'s receipt (SPEI clave de rastreo: 1 to 30 letters or digits / ACH trace / wire IMAD). The bank statement is matched on it.' },
  execute: { title: 'Confirm settlement', hint: 'Enter the CEP folio / comprobante reference you verified. You cannot confirm a transfer you sent yourself.' },
  fail: { title: 'Mark failed', hint: 'The transfer could not be sent or was rejected by the rail. Say why.' },
  return: { title: 'Returned by the bank', hint: 'The bank bounced the transfer; the money is back in the concentration account. Record the bank\'s reason.' },
  reissue: { title: 'Reissue payout', hint: 'Creates a fresh instruction for this leg with the same net amount and destination.' },
  settled_return: {
    title: 'Came back after settlement',
    hint: 'The bank sent back a transfer that was already confirmed as settled. Pick the credit that is this money, enter the bank\'s own reference of the return and its reason. A different manager confirms it.',
  },
  confirm_return: {
    title: 'Confirm the return',
    hint: 'Check the credit and the bank\'s reference against the bank. Confirming returns the payout, takes the credit out of the unattributed queue and tells accounting. You cannot confirm a return you recorded yourself.',
  },
  tie_debit: {
    title: 'Tie bank debit',
    hint: 'Pick the imported bank debit that is this transfer. The same checks apply as at settlement: amount, currency, date, the fund\'s account and the beneficiary. It records evidence and moves no money; the month-end review lists every debit tied this way.',
  },
  decline_return: {
    title: 'Decline the return',
    hint: 'Nothing moves: the payout stays settled and the credit stays in the unattributed queue. Say why.',
  },
};

function ActionPanel({ payout: p, action, onCancel, onDone }: Readonly<{
  payout: Payout; action: Action; onCancel: () => void; onDone: (msg: string) => void;
}>): JSX.Element {
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // At settlement the manager may name the bank debit that is this transfer (picked, never typed).
  const [candidates, setCandidates] = useState<BankDebitCandidate[] | null>(null);
  const [pickedDebit, setPickedDebit] = useState('');

  // A return after settlement names the credit that came back (picked, never typed).
  const [credits, setCredits] = useState<BankDebitCandidate[] | null>(null);
  const [pickedCredit, setPickedCredit] = useState('');
  const [reference, setReference] = useState('');

  useEffect(() => {
    if (action !== 'settled_return') return;
    let live = true;
    payoutsService.returnCredits(p.id)
      .then((list) => {
        if (live) {
          setCredits(list.map((c) => ({ ...c, trace_matches: false, beneficiary_matches: null })));
        }
      })
      .catch((e: unknown) => {
        if (live) {
          setCredits([]);
          setError(getApiErrorMessage(e, 'Could not list the bank credits.'));
        }
      });
    return () => { live = false; };
  }, [action, p.id]);

  useEffect(() => {
    if ((action !== 'execute' && action !== 'tie_debit') || p.settled_bank_transaction_id) return;
    let live = true;
    payoutsService.bankCandidates(p.id)
      .then((list) => { if (live) setCandidates(list); })
      .catch((e: unknown) => {
        if (live) {
          setCandidates([]);
          setError(getApiErrorMessage(e, 'Could not list the bank debits.'));
        }
      });
    return () => { live = false; };
  }, [action, p.id, p.settled_bank_transaction_id]);

  const needsValue = !CODE_ONLY.has(action);
  // What the API accepts: a trace or a proof of at least 4 characters, a reason of at
  // least 5. Checked here so a short reason is not refused after the code is typed.
  const minLength = MIN_VALUE_LENGTH[action] ?? 4;
  const valueReady = !needsValue || value.trim().length >= minLength;
  const returnReady = action !== 'settled_return'
    || (pickedCredit !== '' && reference.trim().length >= 4);
  const tieReady = action !== 'tie_debit' || pickedDebit !== '';
  // Declining a return moves nothing and asks for no code.
  const codeReady = action === 'decline_return' || code.trim().length >= 6;
  const ready = codeReady && valueReady && returnReady && tieReady;

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const mfa = code.trim();
      switch (action) {
        case 'submit':
          await payoutsService.submit(p.id, value.trim(), p.version, mfa);
          onDone('Payout recorded as sent. A different manager must confirm settlement.');
          break;
        case 'execute':
          await payoutsService.execute(p.id, value.trim(), p.version, mfa, pickedDebit || null);
          onDone('Settlement confirmed; the ledger debit is now reconciled.');
          break;
        case 'fail':
          await payoutsService.fail(p.id, value.trim(), p.version, mfa);
          onDone('Payout marked failed. Reissue it once the cause is fixed.');
          break;
        case 'return':
          await payoutsService.markReturned(p.id, value.trim(), p.version, mfa);
          onDone('Payout marked returned. Reissue, or reverse the withdrawal if the client should keep the position.');
          break;
        case 'reissue': {
          await payoutsService.reissue(p.id, mfa);
          onDone('Reissued as a new pending instruction.');
          break;
        }
        case 'settled_return':
          await payoutsService.proposeSettledReturn(p.id, pickedCredit, reference.trim(), value.trim(), mfa);
          onDone('Return recorded. A different manager must confirm it.');
          break;
        case 'confirm_return':
          await payoutsService.confirmSettledReturn(p.id, p.version, mfa);
          onDone('Return confirmed. Reissue, or reverse the withdrawal if the client should keep the position.');
          break;
        case 'tie_debit':
          await payoutsService.tieDebit(p.id, pickedDebit, p.version, mfa);
          onDone('Bank debit tied to this payout.');
          break;
        case 'decline_return':
          await payoutsService.declineSettledReturn(p.id, value.trim());
          onDone('Return declined. The payout stays settled.');
          break;
      }
    } catch (e: unknown) {
      setError(approveMfaErrorMessage(e) || getApiErrorMessage(e, 'Action failed.'));
    } finally {
      setBusy(false);
    }
  };

  const copy = PANEL_COPY[action];
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      <div className="text-sm font-semibold" style={{ color: colors.ink }}>{copy.title}</div>
      <p className="text-xs mb-2" style={{ color: colors.muted }}>{copy.hint}</p>
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {(action === 'submit' || action === 'execute') && (
          <input value={value} onChange={(e) => setValue(e.target.value)}
                 placeholder={action === 'submit' ? 'Rail trace number' : 'CEP / comprobante reference'}
                 className="px-3 py-2 rounded-lg text-sm font-mono sm:col-span-2" style={inputStyle} />
        )}
        {action === 'settled_return' && (
          <>
            <DebitPicker
              name="returned-credit"
              legend="Bank credit that is this money coming back"
              emptyText="No unattributed credit fits this payout's amount, currency and fund account. Import the statement and come back."
              allowNone={false}
              candidates={credits}
              picked={pickedCredit}
              onPick={setPickedCredit}
            />
            <input value={reference} onChange={(e) => setReference(e.target.value)}
                   placeholder="Bank's reference of the return"
                   className="px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
          </>
        )}
        {(action === 'fail' || action === 'return' || action === 'settled_return') && (
          <input value={value} onChange={(e) => setValue(e.target.value)}
                 placeholder="Reason (min 5 characters)"
                 className={`px-3 py-2 rounded-lg text-sm ${action === 'settled_return' ? '' : 'sm:col-span-2'}`}
                 style={inputStyle} />
        )}
        {action === 'decline_return' && (
          <input value={value} onChange={(e) => setValue(e.target.value)}
                 placeholder="Why (min 5 characters)"
                 className="px-3 py-2 rounded-lg text-sm sm:col-span-3" style={inputStyle} />
        )}
        {action === 'tie_debit' && (
          <DebitPicker
            name="late-debit"
            legend="Bank debit that is this transfer"
            emptyText="No imported debit fits this payout's amount, currency, date and fund account. Import the statement and come back."
            allowNone={false}
            candidates={candidates}
            picked={pickedDebit}
            onPick={setPickedDebit}
          />
        )}
        {CODE_ONLY.has(action) && <div className="sm:col-span-2" />}
        {action === 'execute' && (
          <DebitPicker
            tied={p.settled_bank_transaction_id !== null}
            candidates={candidates}
            picked={pickedDebit}
            onPick={setPickedDebit}
          />
        )}
        {action !== 'decline_return' && (
          <input
            type="text" inputMode="numeric" autoComplete="one-time-code" aria-label="Authentication code"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="6-digit code" className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest"
            style={inputStyle}
          />
        )}
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <ActionButton label="Cancel" onClick={onCancel} />
        <button type="button" onClick={() => { run(); }} disabled={busy || !ready}
                className="px-3 py-1.5 rounded-md text-xs font-semibold"
                style={{ background: gradients.gold, color: colors.navy, opacity: busy || !ready ? 0.6 : 1 }}>
          {busy ? '…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: colors.canvas,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
};
